import React from "react";

/**
 * A countdown hook that works with server-provided seconds remaining.
 * Unlike useCountdown, this doesn't rely on client time and simply
 * counts down from the provided number of seconds.
 *
 * @param initialSeconds - Number of seconds remaining until expiry
 * @param callback - Function to call when countdown reaches zero
 * @returns Formatted time string "MM:SS"
 */
const useCountdownFromServer = (
    initialSeconds: number,
    callback: () => void,
) => {
    const hasCalledBack = React.useRef(false);
    const startTime = React.useRef(Date.now());
    const initialSecondsRef = React.useRef(initialSeconds);

    // Update refs when initialSeconds changes
    React.useEffect(() => {
        if (
            initialSeconds !== undefined &&
            initialSeconds !== initialSecondsRef.current
        ) {
            startTime.current = Date.now();
            initialSecondsRef.current = initialSeconds;
            hasCalledBack.current = false;
        }
    }, [initialSeconds]);

    const [timeRemaining, setTimeRemaining] = React.useState(
        initialSeconds ?? 0,
    );

    React.useEffect(() => {
        if (initialSeconds === undefined) return;

        const timer = setInterval(() => {
            // Calculate elapsed time since we started counting
            const elapsedSeconds = Math.floor(
                (Date.now() - startTime.current) / 1000,
            );
            // Calculate remaining time
            const newTimeRemaining = Math.max(
                (initialSeconds ?? 0) - elapsedSeconds,
                0,
            );

            setTimeRemaining(newTimeRemaining);

            if (newTimeRemaining <= 0 && !hasCalledBack.current) {
                hasCalledBack.current = true;
                clearInterval(timer);
                callback();
            }
        }, 1000);

        return () => {
            clearInterval(timer);
        };
    }, [initialSeconds, callback]);

    const clampedTimeRemaining = Math.max(timeRemaining, 0);
    const minutes = Math.floor(clampedTimeRemaining / 60);
    const seconds = Math.floor(clampedTimeRemaining % 60);

    return `${minutes < 10 ? `0${minutes}` : minutes}:${
        seconds < 10 ? `0${seconds}` : seconds
    }`;
};

export default useCountdownFromServer;
